import React, { useEffect, useRef, useState } from "react";
import styles from "./MindspaceSelector.module.css";
import { useMindspaceStore, useAuthStore } from "@store";
import {
  PlusIcon,
  MoreHorizontalIcon,
  EditNoteIcon,
  TrashIcon,
  CheckIcon,
  CloseIcon,
  BrainIcon,
} from "./Icons";

// ─── Types ───────────────────────────────────────────────────────────────
type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "delete"; mindspaceId: string; mindspaceName: string }
  | { kind: "rename"; mindspaceId: string; currentName: string };

export interface MindspaceSelectorProps {
  /** Optional custom trigger element. When provided, replaces the default trigger button. */
  renderTrigger?: (props: {
    onClick: () => void;
    isOpen: boolean;
    activeMindspaceName: string | null;
  }) => React.ReactNode;
  /** Optional extra CSS class for the dropdown wrapper (e.g. for widget positioning). */
  className?: string;
}

// ─── Component ───────────────────────────────────────────────────────────
const MindspaceSelector: React.FC<MindspaceSelectorProps> = ({
  renderTrigger,
  className,
}) => {
  const {
    mindspaces,
    activeMindspaceId,
    setActiveMindspace,
    createMindspace,
    deleteMindspace,
    setDefaultMindspace,
    updateMindspace,
    getActiveMindspace,
    isLoading,
  } = useMindspaceStore();

  const userStatus = useAuthStore((s) => s.userStatus);
  const isGuest = useAuthStore((s) => s.isGuest);

  const activeMindspace = getActiveMindspace();

  // ── Dropdown state
  const [isOpen, setIsOpen] = useState(false);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });

  // ── Inline rename state
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);

  // ── Create form state
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createError, setCreateError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Refs for click-outside
  const dropdownRef = useRef<HTMLDivElement>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // ── Close dropdown on click-outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = (e.composedPath?.()[0] || e.target) as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
        setActionMenuId(null);
        setRenameId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // ── Close action menu on click-outside
  useEffect(() => {
    if (!actionMenuId) return;
    const handler = (e: MouseEvent) => {
      const target = (e.composedPath?.()[0] || e.target) as Node;
      if (actionMenuRef.current && !actionMenuRef.current.contains(target)) {
        setActionMenuId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [actionMenuId]);

  // ── Focus rename input
  useEffect(() => {
    if (renameId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renameId]);

  // ── Guests don't see the selector
  if (isGuest) return null;

  // ── Helpers
  const isPremium = userStatus === "loggedin_premium";
  const canCreate = isPremium || mindspaces.length < 1;

  // ── Handlers
  const handleSelect = (id: string) => {
    setActiveMindspace(id);
    setIsOpen(false);
    setActionMenuId(null);
    setRenameId(null);
  };

  const handleStartRename = (id: string, name: string) => {
    setActionMenuId(null);
    setRenameId(id);
    setRenameValue(name);
  };

  const handleConfirmRename = async () => {
    if (!renameId || !renameValue.trim()) return;
    try {
      await updateMindspace(renameId, { name: renameValue.trim() });
    } catch {
      // Error handled in store
    }
    setRenameId(null);
  };

  const handleCancelRename = () => {
    setRenameId(null);
  };

  const handleSetDefault = async (id: string) => {
    setActionMenuId(null);
    try {
      await setDefaultMindspace(id);
    } catch {
      // Error handled in store
    }
  };

  const handleOpenDeleteConfirm = (id: string, name: string) => {
    setActionMenuId(null);
    setIsOpen(false);
    setModal({ kind: "delete", mindspaceId: id, mindspaceName: name });
  };

  const handleConfirmDelete = async () => {
    if (modal.kind !== "delete") return;
    try {
      await deleteMindspace(modal.mindspaceId);
      setModal({ kind: "closed" });
    } catch {
      // Error handled in store
    }
  };

  const handleOpenCreate = () => {
    setIsOpen(false);
    setCreateName("");
    setCreateDesc("");
    setCreateError("");
    setModal({ kind: "create" });
  };

  const handleConfirmCreate = async () => {
    const name = createName.trim();
    if (!name) {
      setCreateError("Numele este obligatoriu.");
      return;
    }
    if (name.length > 100) {
      setCreateError("Numele nu poate depăși 100 de caractere.");
      return;
    }
    setIsSubmitting(true);
    setCreateError("");
    try {
      const newMs = await createMindspace(name, createDesc.trim() || undefined);
      setModal({ kind: "closed" });
      // Auto-switch to the newly created mindspace
      setActiveMindspace(newMs.id);
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : "Eroare la creare. Încearcă din nou.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Render helpers ──────────────────────────────────────────────────
  const handleToggleDropdown = () => {
    setIsOpen((v) => !v);
    setActionMenuId(null);
    setRenameId(null);
  };

  const wrapperClassName = [styles.dropdownWrapper, className]
    .filter(Boolean)
    .join(" ");

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <>
      <div className={wrapperClassName} ref={dropdownRef}>
        {/* Trigger — custom or default */}
        {renderTrigger ? (
          renderTrigger({
            onClick: handleToggleDropdown,
            isOpen,
            activeMindspaceName: activeMindspace?.name ?? null,
          })
        ) : (
          <button
            className={styles.trigger}
            onClick={handleToggleDropdown}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-label="Selectează mindspace"
          >
            <BrainIcon className={styles.createBtnIcon} title="" />
            <span className={styles.triggerLabel}>
              {activeMindspace?.name || "Selectează mindspace"}
            </span>
            <svg className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        )}

        {/* Dropdown list */}
        {isOpen && (
          <div className={styles.dropdown} role="listbox" aria-label="Mindspace-uri">
            {mindspaces.map((ms) => (
              <div key={ms.id}>
                {renameId === ms.id ? (
                  /* ── Inline rename mode */
                  <div className={styles.item}>
                    <input
                      ref={renameInputRef}
                      className={styles.renameInput}
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleConfirmRename();
                        if (e.key === "Escape") handleCancelRename();
                      }}
                      maxLength={100}
                    />
                    <div className={styles.renameActions}>
                      <button
                        className={styles.renameBtn}
                        onMouseDown={(e) => { e.preventDefault(); handleConfirmRename(); }}
                        title="Salvează"
                      >
                        <CheckIcon className={styles.renameBtnIcon} title="" />
                      </button>
                      <button
                        className={styles.renameBtn}
                        onMouseDown={(e) => { e.preventDefault(); handleCancelRename(); }}
                        title="Anulează"
                      >
                        <CloseIcon className={styles.renameBtnIcon} title="" />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Normal item */
                  <div
                    className={`${styles.item} ${ms.id === activeMindspaceId ? styles.itemActive : ""}`}
                    role="option"
                    aria-selected={ms.id === activeMindspaceId}
                    onClick={() => handleSelect(ms.id)}
                  >
                    <span className={styles.itemName}>{ms.name}</span>
                    {ms.is_default && <span className={styles.defaultBadge} title="Implicit">Default</span>}
                    {ms.id === activeMindspaceId && <CheckIcon className={styles.checkIcon} title="Activ" />}

                    {/* Per-item action button */}
                    <div className={styles.itemActions}>
                      <button
                        className={styles.itemActionsBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActionMenuId(actionMenuId === ms.id ? null : ms.id);
                        }}
                        aria-label="Acțiuni mindspace"
                        title="Acțiuni"
                      >
                        <MoreHorizontalIcon
                          className={styles.itemActionsIcon}
                          width={18}
                          height={18}
                        />
                      </button>

                      {/* Action menu */}
                      {actionMenuId === ms.id && (
                        <div className={styles.actionMenu} ref={actionMenuRef} role="menu">
                          <button
                            className={styles.actionMenuItem}
                            role="menuitem"
                            onClick={(e) => { e.stopPropagation(); handleStartRename(ms.id, ms.name); }}
                          >
                            <EditNoteIcon className={styles.actionMenuIcon} title="" />
                            Redenumește
                          </button>
                          {!ms.is_default && (
                            <button
                              className={styles.actionMenuItem}
                              role="menuitem"
                              onClick={(e) => { e.stopPropagation(); handleSetDefault(ms.id); }}
                            >
                              <span className={styles.actionMenuIcon} style={{ textAlign: "center", fontSize: "14px" }}>★</span>
                              Setează ca implicit
                            </button>
                          )}
                          {!ms.is_default && (
                            <button
                              className={`${styles.actionMenuItem} ${styles.actionMenuDanger}`}
                              role="menuitem"
                              onClick={(e) => { e.stopPropagation(); handleOpenDeleteConfirm(ms.id, ms.name); }}
                            >
                              <TrashIcon className={styles.actionMenuIcon} title="" />
                              Șterge
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Create new button */}
            <div className={styles.separator} />
            <button
              className={styles.createBtn}
              onClick={handleOpenCreate}
              disabled={!canCreate || isLoading}
              title={!canCreate ? "Disponibil cu planul Premium" : "Creează un mindspace nou"}
            >
              <PlusIcon className={styles.createBtnIcon} title="" />
              Creează mindspace nou
              {!canCreate && <span className={styles.upgradeHint}>Premium</span>}
            </button>
          </div>
        )}
      </div>

      {/* ─── Create Modal ─── */}
      {modal.kind === "create" && (
        <div className={styles.modalOverlay} onClick={() => setModal({ kind: "closed" })}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Mindspace nou</h3>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="ms-create-name">Nume</label>
              <input
                id="ms-create-name"
                className={styles.formInput}
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleConfirmCreate(); }}
                placeholder="ex: Client Acme Inc."
                maxLength={100}
                autoFocus
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="ms-create-desc">Descriere <span style={{ color: "#9ca3af" }}>(opțional)</span></label>
              <textarea
                id="ms-create-desc"
                className={styles.formTextarea}
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
                placeholder="O scurtă descriere a proiectului..."
                maxLength={500}
                rows={2}
              />
            </div>
            {createError && <p className={styles.formError}>{createError}</p>}
            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={() => setModal({ kind: "closed" })}>
                Anulează
              </button>
              <button
                className={styles.btnPrimary}
                onClick={handleConfirmCreate}
                disabled={isSubmitting || !createName.trim()}
              >
                {isSubmitting ? "Se creează..." : "Creează"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Confirmation Modal ─── */}
      {modal.kind === "delete" && (
        <div className={styles.modalOverlay} onClick={() => setModal({ kind: "closed" })}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Ștergi mindspace-ul &ldquo;{modal.mindspaceName}&rdquo;?</h3>
            <p className={styles.deleteWarning}>
              Datele de context și documentele asociate vor fi șterse definitiv.
            </p>
            <p className={styles.deleteDetail}>
              Conversațiile vor fi mutate în mindspace-ul implicit.
            </p>
            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={() => setModal({ kind: "closed" })}>
                Anulează
              </button>
              <button className={styles.btnDanger} onClick={handleConfirmDelete} disabled={isLoading}>
                {isLoading ? "Se șterge..." : "Șterge"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MindspaceSelector;
